import React, { useState } from 'react';
import type { GridCell, GridRow, Milestone } from '../../types/life';
import { cn } from '../../utils/cn';
import { distanceParts, plural } from '../../utils/format';
import { DAY_COLUMNS, MONTHS_FULL, formatDateFull } from '../../utils/lifeGrid';
import type { RemainingCounter } from '../../utils/counters';
import { StrikeRow } from '../StrikeRow';
import { RemainingCounters } from '../RemainingCounters';
interface HoverInfo {
  title: string;
  start: Date;
  milestone?: string;
  x: number;
  y: number;
}
interface NowViewProps {
  stripRows: GridRow[];
  stripColumns: number;
  dayRows: GridRow[];
  year: number;
  now: Date;
  compact: boolean;
  counters: RemainingCounter[];
  milestoneByCell: Map<string, Milestone>;
  focusedKey: string | null;
  onSelectYear: (cell: GridCell) => void;
  onSelectMonth: (row: GridRow) => void;
  onSelectDay: (cell: GridCell) => void;
}
export function NowView({
  stripRows,
  stripColumns,
  dayRows,
  year,
  now,
  compact,
  counters,
  milestoneByCell,
  focusedKey,
  onSelectYear,
  onSelectMonth,
  onSelectDay
}: NowViewProps) {
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const daysLeftInYear = Math.max(0, Math.ceil((new Date(year + 1, 0, 1).getTime() - now.getTime()) / 86_400_000));
  const openHover = (title: string, start: Date, milestone?: string) => (event: React.SyntheticEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHover({
      title,
      start,
      milestone,
      x: rect.left + rect.width / 2,
      y: rect.top
    });
  };
  const distance = hover ? distanceParts(hover.start, now) : null;
  const tooltipX = hover ? Math.min(Math.max(hover.x, 130), (typeof window !== 'undefined' ? window.innerWidth : 1200) - 130) : 0;
  return <div className={compact ? 'space-y-8' : 'space-y-12'} onMouseLeave={() => setHover(null)} data-id="element-177" data-mp="n3brynrnnnyz">
      <section aria-label="Years of your life" className={compact ? 'space-y-3' : 'space-y-4'} data-id="element-178" data-mp="s2oc30r56gxv">
        {stripRows.map(row => <StrikeRow key={row.key} columns={stripColumns} gapPx={compact ? 8 : 12} labelWidth={0} fraction={row.fraction} data-id="element-179" data-mp="2ts3xnfyizs4c">
            {row.cells.map(cell => {
          if (cell.state === 'void') return <span key={cell.key} aria-hidden data-id="element-180" data-mp="2g0fn3h3xdbph" />;
          const milestone = milestoneByCell.get(cell.key);
          const focused = focusedKey === cell.key;
          const enter = openHover(cell.label, cell.start, milestone?.title);
          return <button key={cell.key} type="button" data-cell={cell.key} onClick={() => onSelectYear(cell)} onMouseEnter={enter} onFocus={enter} onBlur={() => setHover(null)} aria-label={milestone ? `${cell.label}, milestone: ${milestone.title}` : cell.label} className={cn('relative px-0.5 py-1 font-semibold tracking-tight transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', compact ? 'text-center text-sm' : 'text-left text-base md:text-lg', cell.state === 'past' && 'text-ink', cell.state === 'future' && 'text-muted hover:text-ink', cell.state === 'current' && 'text-accent', milestone && 'text-accent', focused && 'bg-accent-soft')} data-id="element-181" data-mp="33pi4qoyk74jz">
                  {cell.label}
                  {(milestone || cell.state === 'current') && <span className="bg-accent absolute -right-1 top-0.5 h-1.5 w-1.5 rounded-full" aria-hidden data-id="element-182" data-mp="2au0ttin2yc1w" />}
                </button>;
        })}
          </StrikeRow>)}
      </section>

      <section aria-label={`Current year: ${year}`} className="space-y-3" data-id="element-183" data-mp="ghaweqxi6kpr">
        <div className="border-line flex items-baseline justify-between gap-4 border-b pb-2" data-id="element-184" data-mp="26wtmsyblki80">
          <h2 className="text-ink font-mono text-sm" data-id="element-185" data-mp="21ya0uxx076fn">{year}</h2>
          <p className="text-muted text-xs" data-id="element-186" data-mp="2fykta5dyot2x">
            <span className="text-accent font-mono tabular-nums" data-id="element-187" data-mp="2fyb3oiwtwnxr">{daysLeftInYear}</span>{' '}
            {plural(daysLeftInYear, 'day')} left
          </p>
        </div>

        <div className={compact ? 'space-y-1.5' : 'space-y-2'} data-id="element-188" data-mp="1n9dxvf9t0jlw">
          {dayRows.map(row => {
          const monthMilestone = milestoneByCell.get(row.key);
          const monthFocused = focusedKey === row.key;
          const monthStart = row.cells[0].start;
          const monthTitle = `${MONTHS_FULL[monthStart.getMonth()]} ${year}`;
          const enterMonth = openHover(monthTitle, monthStart, monthMilestone?.title);
          return <StrikeRow key={row.key} columns={DAY_COLUMNS} gapPx={compact ? 3 : 6} labelWidth={compact ? 34 : 52} fraction={row.fraction} thickness={1.5} label={<button type="button" data-cell={row.key} onClick={() => onSelectMonth(row)} onMouseEnter={enterMonth} onFocus={enterMonth} onBlur={() => setHover(null)} aria-label={monthMilestone ? `${monthTitle}, milestone: ${monthMilestone.title}` : monthTitle} className={cn('relative text-left font-semibold leading-none transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', compact ? 'text-[11px]' : 'text-sm', monthMilestone ? 'text-accent' : 'text-ink hover:text-accent', monthFocused && 'bg-accent-soft')} data-id="element-190" data-mp="3fzy1jl4s6w98">
                    {row.label}
                    {monthMilestone && <span className="bg-accent absolute -right-1 top-0 h-1.5 w-1.5 rounded-full" aria-hidden data-id="element-191" data-mp="1lmzdlmbx6miu" />}
                  </button>} data-id="element-189" data-mp="14hbc5pib2d4s">
                {row.cells.map(cell => {
              if (cell.state === 'void') return <span key={cell.key} aria-hidden data-id="element-192" data-mp="1bmp1y65j9n5s" />;
              const milestone = milestoneByCell.get(cell.key);
              const focused = focusedKey === cell.key;
              const accent = Boolean(milestone) || cell.state === 'current';
              const dayTitle = formatDateFull(cell.start);
              const enterDay = openHover(dayTitle, cell.start, milestone?.title);
              const label = milestone ? `${dayTitle}, milestone: ${milestone.title}` : dayTitle;

              // numbers do not fit into 10px columns on a narrow screen — draw evenly spaced dots
              if (compact) {
                return <button key={cell.key} type="button" data-cell={cell.key} onClick={() => onSelectDay(cell)} onMouseEnter={enterDay} onFocus={enterDay} onBlur={() => setHover(null)} aria-label={label} className="flex h-4 items-center justify-center focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent" data-id="element-193" data-mp="34kck2umb8ph6">
                        <span aria-hidden className={cn('rounded-full', accent ? 'bg-accent' : cell.state === 'past' ? 'bg-ink' : 'bg-faint', focused && 'ring-accent ring-2')} style={{
                    width: accent ? 6 : 4,
                    height: accent ? 6 : 4,
                    boxShadow: accent ? '0 0 0 2px var(--paper)' : undefined
                  }} data-id="element-194" data-mp="2unzotsb4a3kb" />
                      </button>;
              }
              return <button key={cell.key} type="button" data-cell={cell.key} onClick={() => onSelectDay(cell)} onMouseEnter={enterDay} onFocus={enterDay} onBlur={() => setHover(null)} aria-label={label} className={cn('relative py-0.5 text-center text-[11px] tabular-nums transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', cell.state === 'past' && 'text-ink', cell.state === 'future' && 'text-muted hover:text-ink', cell.state === 'current' && 'text-accent font-semibold', milestone && 'text-accent font-semibold', focused && 'bg-accent-soft')} data-id="element-195" data-mp="1eo8rjx593uqo">
                      {cell.label}
                      {accent && <span className="bg-accent absolute right-0 top-0 h-1 w-1 rounded-full" aria-hidden data-id="element-196" data-mp="1m9bbhgrhqxt0" />}
                    </button>;
            })}
              </StrikeRow>;
        })}
        </div>
      </section>

      {counters.length > 0 && <section className="border-line border-t pt-4" aria-label="Still to come" data-id="element-197" data-mp="32ockiqnskzvc">
          <RemainingCounters items={counters} data-id="element-198" data-mp="37mxlisavbnku" />
        </section>}

      {hover && distance && <div role="tooltip" className="pointer-events-none fixed z-40 -translate-x-1/2 -translate-y-full" style={{
      left: tooltipX,
      top: Math.max(hover.y - 10, 84)
    }} data-id="element-199" data-mp="zkyojay3z9ik">
          <div className="bg-ink text-paper px-2.5 py-2 font-mono text-[11px] leading-snug shadow-xl" data-id="element-200" data-mp="qlleoh62oixb">
            <p className="font-medium" data-id="element-201" data-mp="vkok42pysr5t">{hover.title}</p>
            <p style={{
          opacity: 0.75
        }} data-id="element-202" data-mp="1j97ssjcxf5yf">{distance.main}</p>
            {distance.alt && <p style={{
          opacity: 0.55
        }} data-id="element-203" data-mp="1j73fjat50ulh">{distance.alt}</p>}
            {hover.milestone && <p className="text-accent-soft mt-1 font-medium" data-id="element-204" data-mp="1na9j2aov87o9">{hover.milestone}</p>}
          </div>
        </div>}
    </div>;
}